import { useRef, useState } from 'react'
import { createStructuredSelector } from 'reselect'
import { withTranslate } from '@i18n/utils'

import Modal from '@components/Modal'
import Tabs, { Tab } from '@components/Tabs'

import BillingTab from 'dashboard-subscriptions/components/BillingTab'
import InvoicesTab from 'dashboard-subscriptions/components/InvoicesTab'
import ParkersTab from 'dashboard-subscriptions/components/ParkersTab'
import SubscriptionModalHeader from 'dashboard-subscriptions/components/SubscriptionModalHeader'

import { clearPending } from 'api/actions'
import { PAYMENT_METHOD_TYPE_PAD } from 'auth/constants'
import {
    SUBSCRIPTION_MODAL_TAB_BILLING,
    SUBSCRIPTION_MODAL_TAB_INVOICES,
    SUBSCRIPTION_STATUS_ACTIVE,
    UPDATE_SUBSCRIPTION_PROGRESS,
} from 'dashboard-subscriptions/constants'
import { subscriptionSelector } from 'dashboard-subscriptions/selectors'

import styles from './styles.module.scss'

const SubscriptionModal = ({ onClose, subscription, initialTab, clearPending, translate: t }) => {
    const [currentTab, setCurrentTab] = useState(initialTab)

    const paymentMethodType = subscription.paymentMethods && subscription.paymentMethods[0]?.paymentMethodType
    const paidWithPad = paymentMethodType === PAYMENT_METHOD_TYPE_PAD
    const isActiveSubscription = subscription.state === SUBSCRIPTION_STATUS_ACTIVE
    const canShowPayBtn = subscription.balance > 0 && currentTab !== SUBSCRIPTION_MODAL_TAB_INVOICES && !paidWithPad

    const tabRef = useRef()
    const billingRef = React.createRef()

    const closeModal = () => {
        // This modal can have additional modals open.
        // Outside click handles all child modals as outside as well (due to React.Portal)
        // This checks if there are more modal open. If there is, onClose is ignored.
        // Only additional modals open, can be child modals.
        const modals = document.getElementsByClassName('modal-dropdown')
        if (modals.length === 1 && handleUnsavedChanges()) {
            clearPending([UPDATE_SUBSCRIPTION_PROGRESS])
            onClose()
        }
    }

    const tabChangeCallback = index => {
        setCurrentTab(index)
    }

    const goPay = () => {
        tabRef.current?.click(SUBSCRIPTION_MODAL_TAB_INVOICES)
    }

    const handleUnsavedChanges = () => {
        if (billingRef?.current?.isChanged()) {
            return confirm(t('warning.unsaved.changes'))
        }

        return true
    }

    return (
        <Modal className={styles.modal} size="large" withoutOutsideClick={false} onClose={closeModal}>
            <SubscriptionModalHeader
                goPay={goPay}
                subscription={subscription}
                isActiveSubscription={isActiveSubscription}
                showPayBtn={canShowPayBtn}
            />
            <Tabs
                activeTab={initialTab || SUBSCRIPTION_MODAL_TAB_BILLING}
                tabStyle="pills"
                className={styles.modalTabsContainer}
                tabChangeCallback={tabChangeCallback}
                ref={tabRef}
                tabClickHandler={handleUnsavedChanges}>
                <Tab className={styles.modalTab} label={<T>dashboard.subscriptions.modal.tab.billing</T>}>
                    <BillingTab billingRef={billingRef} subscription={subscription} closeModal={closeModal} />
                </Tab>
                <Tab className={styles.modalTab} label={<T>dashboard.subscriptions.modal.tab.products</T>}>
                    <ParkersTab
                        products={subscription.members || []}
                        closeModal={closeModal}
                        subscriptionId={subscription.id}
                    />
                </Tab>
                <Tab className={styles.modalTab} label={<T>dashboard.subscriptions.modal.tab.invoices</T>}>
                    <InvoicesTab
                        subscriptionId={subscription.id}
                        isActiveSubscription={isActiveSubscription}
                        paidWithPad={paidWithPad}
                        closeModal={closeModal}
                    />
                </Tab>
            </Tabs>
        </Modal>
    )
}

const mapStateToProps = (state, ownProps) =>
    createStructuredSelector({
        subscription: subscriptionSelector(ownProps.id),
    })

export default compose(withTranslate, connect(mapStateToProps, { clearPending }))(SubscriptionModal)
